import { useContext } from 'react';
import { getApolloContext } from '@apollo/client';
import { StationeryProductsDocument, useStationeryProductsQuery } from './useProducts.graphql.generated';
import type { StationeryProductsQuery } from './useProducts.graphql.generated';
import type { StationeryProduct } from '@shared/utils/print-price/types';

const ZERO_PRODUCTS: StationeryProduct[] = []; // For stability

/**
 * Whether the products catalog is already in the Apollo cache — on the server because
 * `prefetchStationeryProducts` put it there for a crawlable `format:*` / `product:magnet` gallery
 * URL, on the client because `extract()` inlined that same cache as `window.__INITIAL_APOLLO_STATE__`
 * (PRNT-5582). The client's first render reads the server's own `extract()` back, which keeps the
 * two halves of `useIsPublicGallerySsr` in step.
 *
 * Not reactive, so a later client fetch can move it false → true without re-rendering. Left that
 * way deliberately: no consumer acts on a flip — `useDynamicBannerState` takes its banner as a
 * `useState` initialiser, `useValuePropsPayload` gates on `!isMounted`, and `useDesignTemplates`
 * only un-skips a `cache-only` summary that full data outranks. Freeze it app-wide (as
 * `ThemeSearchIndexProvider` does for the index) if one ever does.
 *
 * `stationeryProducts` takes no arguments, so the entry is keyed the same however it was written —
 * the `batchMode` variable every caller adds is not part of the cache key.
 */
export const useHasStationeryProducts = (): boolean => {
  // The context directly rather than `useApolloClient`, which throws without a provider: the gate
  // needs "no client" to answer false, its conservative default everywhere else.
  const { client } = useContext(getApolloContext());
  if (!client) return false;
  try {
    return !!client.readQuery<StationeryProductsQuery>({ query: StationeryProductsDocument })?.stationeryProducts?.length;
  } catch {
    // A partial cache makes `readQuery` throw rather than answer null; that is "not usable here".
    return false;
  }
};

/**
 * The product list plus whether it has settled.
 *
 * Callers that only render the list can use `useProducts` and ignore this — an empty array renders
 * as nothing either way. A caller that *acts* on the list needs the flag: an empty array is both
 * "not back yet" and "genuinely none", and treating the second as the first hangs forever.
 */
export const useProductsWithStatus = (): { products: StationeryProduct[]; isLoading: boolean } => {
  // Apollo skips an `ssr: false` query outright during SSR — it would not even read the seeded
  // cache — so this opts in only where the catalog is already there, never issuing a request.
  const hasPrefetchedProducts = useHasStationeryProducts();
  const {
    data: productData,
    loading,
    error: productsError,
  } = useStationeryProductsQuery({
    batchMode: 'fast',
    fetchPolicy: 'cache-first',
    ssr: hasPrefetchedProducts,
  });

  if (productsError) throw productsError;

  return { products: productData?.stationeryProducts ?? ZERO_PRODUCTS, isLoading: loading && !productData };
};

export const useProducts = (): StationeryProduct[] => useProductsWithStatus().products;
